import { useEffect, useRef, useState } from 'react';
import { fetchSiteSettings, updateSiteSettings, uploadImage } from '@/lib/api';
import { useCollection } from '@/hooks/useCollection';
import type { HeroSlide, MediaItem } from '@/lib/types';

const EMPTY_SLIDE: HeroSlide = { image: '', headline: '', subtext: '' };

export default function HeroSlidesManager() {
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickerIdx, setPickerIdx] = useState<number | null>(null);
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const fileTarget = useRef<number | null>(null);
  const { rows: media } = useCollection<MediaItem>('media_library', 'created_at', false);

  useEffect(() => {
    fetchSiteSettings()
      .then((s) => setSlides(s.hero_images?.length ? s.hero_images : []))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load hero slides'))
      .finally(() => setLoading(false));
  }, []);

  const update = (idx: number, patch: Partial<HeroSlide>) =>
    setSlides((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const move = (idx: number, dir: -1 | 1) => {
    setSlides((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const remove = (idx: number) => setSlides((prev) => prev.filter((_, i) => i !== idx));

  const addSlide = () => setSlides((prev) => [...prev, { ...EMPTY_SLIDE }]);

  const triggerUpload = (idx: number) => {
    fileTarget.current = idx;
    fileRef.current?.click();
  };

  const onFilePicked = async (file: File | undefined) => {
    const idx = fileTarget.current;
    if (!file || idx === null) return;
    setUploadingIdx(idx);
    setError(null);
    try {
      const url = await uploadImage(file, 'hero');
      update(idx, { image: url });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploadingIdx(null);
      fileTarget.current = null;
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async () => {
    const cleaned = slides
      .map((s) => ({ image: s.image.trim(), headline: s.headline.trim(), subtext: s.subtext.trim() }))
      .filter((s) => s.image || s.headline);
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      await updateSiteSettings({ hero_images: cleaned });
      setMessage('Hero slides saved. Refresh the homepage to see the changes.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save hero slides');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="bg-white rounded-lg border border-background-200 p-6">
        <p className="text-center text-foreground-400 py-6">
          <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading hero slides...
        </p>
      </section>
    );
  }

  const inputClass =
    'w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900';

  return (
    <section className="bg-white rounded-lg border border-background-200 p-6">
      <div className="flex items-center gap-2 mb-1">
        <i className="ri-slideshow-3-line text-primary-600" aria-hidden="true" />
        <h3 className="font-heading font-semibold text-base text-foreground-900">Homepage Hero Slides</h3>
      </div>
      <p className="text-sm text-foreground-500 mb-5">
        Manage the rotating banners at the top of the homepage. Reorder slides, edit each headline and subtext, and
        choose a photo — upload a new one or pick from your media library.
      </p>

      {message && (
        <div className="mb-4 px-4 py-3 rounded-md bg-accent-100 text-accent-900 text-sm border border-accent-300">{message}</div>
      )}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">{error}</div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onFilePicked(e.target.files?.[0])}
      />

      <div className="space-y-4">
        {slides.map((slide, idx) => (
          <div key={idx} className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Preview */}
              <div className="w-full sm:w-40 shrink-0">
                <div className="h-24 w-full rounded-lg overflow-hidden border border-background-200 bg-background-100">
                  {slide.image ? (
                    <img src={slide.image} alt={`Slide ${idx + 1}`} className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-foreground-300">
                      <i className="ri-image-line text-2xl" aria-hidden="true" />
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <button
                    type="button"
                    onClick={() => triggerUpload(idx)}
                    disabled={uploadingIdx === idx}
                    className="text-[11px] font-label px-2.5 py-1.5 rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {uploadingIdx === idx ? (
                      <><i className="ri-loader-4-line animate-spin mr-1" aria-hidden="true" />Uploading</>
                    ) : (
                      <><i className="ri-upload-2-line mr-1" aria-hidden="true" />Upload</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPickerIdx(idx)}
                    className="text-[11px] font-label px-2.5 py-1.5 rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-folder-image-line mr-1" aria-hidden="true" />Library
                  </button>
                </div>
              </div>

              {/* Fields */}
              <div className="flex-1 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-label font-semibold uppercase tracking-widest text-foreground-500">
                    Slide {idx + 1}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      title="Move up"
                      className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 disabled:opacity-30 cursor-pointer"
                    >
                      <i className="ri-arrow-up-line" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, 1)}
                      disabled={idx === slides.length - 1}
                      title="Move down"
                      className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 disabled:opacity-30 cursor-pointer"
                    >
                      <i className="ri-arrow-down-line" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(idx)}
                      title="Remove slide"
                      className="w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 cursor-pointer"
                    >
                      <i className="ri-delete-bin-line" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Headline</label>
                  <input
                    type="text"
                    value={slide.headline}
                    onChange={(e) => update(idx, { headline: e.target.value })}
                    placeholder="e.g. Empowering Youth Through Faith"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Subtext</label>
                  <textarea
                    value={slide.subtext}
                    onChange={(e) => update(idx, { subtext: e.target.value })}
                    placeholder="A short supporting sentence shown under the headline."
                    rows={2}
                    className={`${inputClass} resize-y`}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        {slides.length === 0 && (
          <div className="text-center py-10 border border-dashed border-background-300 rounded-lg">
            <i className="ri-slideshow-line text-3xl text-foreground-300" aria-hidden="true" />
            <p className="mt-2 text-sm text-foreground-500">No slides yet. Add your first hero slide.</p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
        <button type="button" onClick={addSlide} className="btn-outline whitespace-nowrap cursor-pointer">
          <i className="ri-add-line" aria-hidden="true" />
          Add Slide
        </button>
        <button type="button" onClick={save} disabled={saving} className="btn-primary whitespace-nowrap cursor-pointer">
          {saving ? 'Saving...' : 'Save Hero Slides'}
        </button>
      </div>

      {pickerIdx !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPickerIdx(null)} aria-hidden="true" />
          <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
              <h3 className="font-heading font-semibold text-lg text-foreground-900">Choose from Media Library</h3>
              <button
                type="button"
                onClick={() => setPickerIdx(null)}
                className="p-2 rounded-md hover:bg-background-100 cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {media.length === 0 ? (
                <p className="text-center text-foreground-400 py-10 text-sm">
                  Your media library is empty. Upload photos in the Media Library section first.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {media.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        update(pickerIdx, { image: item.url });
                        setPickerIdx(null);
                      }}
                      className="group rounded-lg overflow-hidden border border-background-200 hover:border-primary-400 transition-colors cursor-pointer text-left"
                    >
                      <div className="h-28 w-full overflow-hidden">
                        <img src={item.url} alt={item.title || 'Library photo'} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform" />
                      </div>
                      <p className="text-[11px] text-foreground-600 px-2 py-1.5 line-clamp-1">{item.title || 'Untitled'}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}